import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { DialogCloseResult, DialogModule, DialogService } from '@progress/kendo-angular-dialog';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { ApiService } from '../../api/api.service';
import { AuthTokenService } from '../auth-token.service';
import { AuthService } from '../auth.service';
import { ToastrService } from 'ngx-toastr';
import { UserDataService } from '../../users/user-data.service';
import { SettingsService } from '../../settings/settings.service';
import { TimeoutService } from '../timeout.service';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { InactiveAlertComponent } from '../inactive-alert/inactive-alert.component';
import { captchaExpiryMinutes, CasiCloseOptions, PHONE_REGEX_V2 } from '../../common/constants.lib';
import { TermsConditionsService } from '../../common-pages/terms-conditions.service';
import { passwordMatchValidator } from '../../common/utils.lib';
import { CaptchaComponent } from '../../custom-accessors/captcha/captcha.component';
import { AccountApplication, TermsResponse } from '../../common/obj.lib';
import { firstOrLastNameRequiredValidator } from '../../directives/casi.validators';
import { CaptchaRequiredComponent } from '../../custom-accessors/captcha-required/captcha-required.component';
import { CaptchaServiceService } from '../../shared/captcha-service.service';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';

@Component({
  selector: 'casi-signup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggleModule,
    NotificationModule,
    RouterLink,
    RouterLinkActive,
    DialogModule,
    FontAwesomeModule,
    AutoFocusDirective,
    // CaptchaComponent,
    CaptchaRequiredComponent,
    ErrorDialogModalComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})
export class SignupComponent implements OnInit, OnDestroy {

  signupForm: FormGroup;
  busy: boolean = false;
  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;
  private returnUrl: string | null = null;

  private docVersion: number = 0;
  docContent: string = "";
  private _subs: Subscription[] = [];
  public showTermsDialog = false;
  showCaptcha = false;
  focusCounter: number = 0;

  constructor(
    private logger: LoggerService,
    private fb: NonNullableFormBuilder,
    private apiService: ApiService,
    private authTokenService: AuthTokenService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private dialogService: DialogService,
    private userDataService: UserDataService,
    private settings: SettingsService,
    public faIcons: FaIconLibrary,
    private captchaService: CaptchaServiceService,
    private dataService: TermsConditionsService
  ) {
    faIcons.addIcons(...all);

    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
      first_name: [null],
      last_name: [null],
      phone: [null, [Validators.pattern(PHONE_REGEX_V2)]],
      agreeToTerms: [false, [Validators.requiredTrue]],
      research_consent: [true, []],
      newsletter: [true, []],
      // captchaValid: [null, [Validators.required]]

    }, {
      validators: [
        firstOrLastNameRequiredValidator,
        passwordMatchValidator
      ]
    });

  }

  //#region Form

  get f() {
    return this.signupForm.controls;
  }

  get email() {
    return this.signupForm.get('email');
  }
  get password() {
    return this.signupForm.get('password');
  }

  get first_name() {
    return this.signupForm.get('first_name');
  }
  get last_name() {
    return this.signupForm.get('last_name');
  }
  get phone() {
    return this.signupForm.get('phone');
  }

  get agreeToTerms() {
    return this.signupForm.get('agreeToTerms');
  }

  get research_consent() {
    return this.signupForm.get('research_consent');
  }

  get newsletter() {
    return this.signupForm.get('newsletter');
  }
  get captchaValid() {
    return this.signupForm.get('captchaValid');
  }

  //#endregion

  //#region Ng Handlers

  ngOnInit(): void {
    this._subs.push(this.route.queryParams.subscribe(params => {
      if (params['returnUrl'])
        this.returnUrl = params['returnUrl'];
    }));

    // this._subs.push(this.dataService.doc$.subscribe((docs: TermsResponse) => {
    //   this.logger.debug(`ngOnInit:terms:`, docs);
    //   this.docVersion = docs.version;
    //   this.docContent = docs.content;

    // }))
    this.dataService.get("terms")
      .then(response => {
        this.logger.debug('ngOnInit: ', response);
        this.docVersion = response.version;
        this.docContent = response.html;
      }).catch(err => {
        this.logger.error('ngOnInit: ', err);
      });

    this.signupForm.statusChanges.subscribe(() => {
      const confirm = this.signupForm.get('confirmPassword');
      if (!confirm) return;

      const hasMismatch = this.signupForm.errors?.['passwordMismatch'];

      if (hasMismatch && !confirm.hasError('passwordMismatch')) {
        confirm.setErrors({ ...(confirm.errors || {}), passwordMismatch: true });
      }

      if (!hasMismatch && confirm.hasError('passwordMismatch')) {
        const errors = { ...(confirm.errors || {}) };
        delete errors['passwordMismatch'];
        confirm.setErrors(Object.keys(errors).length ? errors : null);
      }
    });

    this.email?.valueChanges.subscribe(value => {
      if (value && value !== value.toLowerCase()) {
        this.email?.setValue(value.toLowerCase(), { emitEvent: false });
      }
    });
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Public methods

  setForm() {
    this.signupForm.patchValue({});
    this.removeCaptchaValid();
    this.showCaptcha = false;
  }

  sendClicked() {
    this.logger.debug(`sendClicked...`);
    this.addCaptchaValid();
    this.showCaptcha = true;
    this.resetCaptchaText();
  }

  resetForm() {
    this.logger.debug('resetForm...');
    this.signupForm.reset();
    this.setForm();
  }

  onSubmit() {
    this.logger.debug('onSubmit...', this.signupForm.value);
    const { captchaValid } = this.signupForm.value;

    let toSubmit = AccountApplication.create(this.signupForm.value, this.docVersion);
    this.settings.lastLogin = toSubmit.lastLogin;
    this.logger.debug('onSubmit...', toSubmit.forSubmit());
    return this.verifyBeforeSubmit(captchaValid)
      .then(resp => {
        this.logger.debug(`onSubmit:resp: `, resp);
        if (resp.iamhuman === true) {
          this.submitAccount(toSubmit);
        } else {
          this.logger.error(`onSubmit: Not valid!`);
          this.resetCaptchaText();
        }
      });
  }

  openTerms() {
    this.showTermsDialog = true;
  }

  closeTerms() {
    this.showTermsDialog = false;
  }

  captchaHandler(evt: any) {
    this.logger.debug(`captchaHandler: evt: `, evt);
  }

  imageLoaded(evt: any) {
    this.logger.debug(`imageLoaded: evt: `, evt);
    // if (evt === true) {
    //   setTimeout(() => this.dataService.get("terms"), 500);
    // }
  }

  //#endregion

  //#region Private methods

  private addCaptchaValid() {
    this.handleFormCaptchaValid();
  }
  private removeCaptchaValid() {
    this.handleFormCaptchaValid(false);
  }
  private handleFormCaptchaValid(flag: boolean = true) {
    if (flag) {
      this.signupForm.addControl('captchaValid', new FormControl('', Validators.required));
    } else {
      this.signupForm.removeControl('captchaValid');
    }
  }

  private submitAccount(toSubmit: AccountApplication) {
    this.logger.debug('submitAccount...', toSubmit.forSubmit());
    this.busy = true;
    this.authService
      .signup(toSubmit.forSubmit())
      .then(resData => {
        this.logger.debug('submitAccount:resData: ', resData);
        this.authTokenService.setToken(resData);
        this.apiService.getCsrfAsPromise();
        this.signupForm.reset();
        this.router.navigate(['/home']);
      })
      .catch(err => {
        this.logger.error('onSubmit: error: ', err);
        let emailAddressAlreadyInUse = err.status == 409;
        if (emailAddressAlreadyInUse) {          // this.toastr.error('Email address already in use.', 'Signup', {
          //   positionClass: 'toast-center-center'
          // });
          this.notify('Email address already in use.', true);
          this.resetCaptchaText();
        }
      })
      .finally(() => this.busy = false);
  }

  private resetCaptchaText() {
    this.signupForm.patchValue({
      captchaValid: ''
    })
  }
  private verifyBeforeSubmit(captchaText: string = ''): Promise<any> {
    return this.captchaService
      .verifyCaptchaInputTextOnly(captchaText.toUpperCase(), true)
      .then(resp => {
        this.logger.debug(`verifyBeforeSubmit:resp: `, resp);
        if (!resp.iamhuman === true) {
          this.openErrorDailog("INCORRECT OR EXPIRED CAPTCHA",
            `This CAPTCHA is either incorrect or expired. CAPTCHAs expire after ${captchaExpiryMinutes} minutes
          for security purposes. Please recapture a new value.`,
            "OK");
        }
        return resp;
      })
      .catch(err => {
        this.logger.error(`verifyBeforeSubmit:error: `, err.message);
        return Promise.reject(err);
      })
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close'): void {
    this.logger.debug(`openErrorDialog: `, {
      title,
      content,
      buttonText
    });
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      appendTo: this.appendTo,
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openErrorDailog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Closed) {
        this.logger.debug("openErrorDailog:ok to proceed... ");
        this.focusCounter++;
      }
    });

  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  //#endregion


}
